import React from 'react';
import { Activity, Info, Zap } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useDashboard } from '../../DashboardContext';
import { S, SCENARIO_LABELS, annualizedROI } from '../../lib/constants';
import SortedTooltip from '../shared/SortedTooltip';
import ChartLegend from '../shared/ChartLegend';

/* ── X-axis tick formatter: show "Yr 1", "Yr 2" at year boundaries, else month ── */
const monthTick = (month, timeHorizon) => {
  if (timeHorizon <= 2) return `Mo ${month}`;
  if (month % 12 === 0) return `Yr ${month / 12}`;
  if (month === 1) return 'Mo 1';
  return '';
};

export default function OverviewSection() {
  const {
    activeStrategy,
    activeCalc,
    displayCalculations,
    comparisonData,
    currentStats,
    timeHorizon,
    blockReward,
    difficulty,
    priceScenario,
    equivalentDCA,
    miningEconomics,
    fmt, fBTC,
  } = useDashboard();

  const scenarioLabel = SCENARIO_LABELS[priceScenario] || '';

  const legendItems = displayCalculations.map(calc => ({
    name: calc.strategy.name,
    color: calc.strategy.color,
    dashed: calc.strategy.isBenchmark,
  }));

  /* Helper: chart subtitle */
  const chartMeta = {
    nav:      { title: 'Net Asset Value',      subtitle: 'Total portfolio value (BTC holdings + cash)', accentColor: '#2563EB' },
    treasury: { title: 'BTC Treasury',         subtitle: 'Cumulative BTC held over time',              accentColor: '#92700C' },
    roi:      { title: 'Return on Investment', subtitle: 'Percent return on invested capital',          accentColor: '#16A34A' },
    cash:     { title: 'Cash Balance',         subtitle: 'Available USD cash over time',                accentColor: '#0891B2' },
  };

  return (
    <div className="space-y-6">
      {/* ── Month 1 Daily Metrics ── */}
      <div className="rounded-lg overflow-hidden" style={S.card}>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F5F5F5' }}>
              <Activity className="w-4 h-4" style={{ color: '#16A34A' }} />
            </div>
            <div>
              <h3 className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>Month 1 Daily Metrics</h3>
              <p className="text-xs" style={{ color: '#737373' }}>Based on current settings</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Daily BTC', value: fBTC(currentStats.btcPerDay, 6), color: '#92700C', sub: 'mined per day' },
              { label: 'Daily Revenue', value: `$${(currentStats.dailyRevenue || 0).toFixed(2)}`, color: '#16A34A', sub: 'gross income' },
              { label: 'Daily Power', value: `$${(currentStats.dailyCost || 0).toFixed(2)}`, color: '#DC2626', sub: 'electricity cost' },
              { label: 'Daily Profit', value: `$${(currentStats.dailyProfit || 0).toFixed(2)}`, color: (currentStats.dailyProfit || 0) >= 0 ? '#16A34A' : '#DC2626', sub: 'net earnings' },
              { label: 'Monthly BTC', value: fBTC(currentStats.btcMined, 4), color: '#92700C', sub: 'total mined' },
              { label: 'Difficulty', value: `${difficulty.toFixed(2)} T`, color: '#2563EB', sub: 'network difficulty' },
            ].map((m, i) => (
              <div key={i} className="rounded p-3 transition-all" style={S.innerCard}>
                <div className="text-xs mb-1" style={{ color: '#737373', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</div>
                <div className="text-lg font-bold" style={{ color: m.color, fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
                <div className="text-xs mt-0.5" style={{ color: '#737373' }}>{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Halving Info ── */}
      {activeCalc?.halvingsInPeriod > 0 && (
        <div className="rounded-lg p-4 flex items-center gap-3" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5' }}>
          <Info className="w-5 h-5 flex-shrink-0" style={{ color: '#7C3AED' }} />
          <span className="text-sm" style={{ color: '#333333' }}>
            <strong style={{ color: '#7C3AED' }}>{activeCalc.halvingsInPeriod} halving{activeCalc.halvingsInPeriod > 1 ? 's' : ''}</strong> in {timeHorizon}yr period. Block reward falls from {blockReward} to {(blockReward / Math.pow(2, activeCalc.halvingsInPeriod)).toFixed(4)} BTC
          </span>
        </div>
      )}

      {/* ── Winner Cards + Mining Multiplier + Acquisition Economics + Comparison Table ── */}
      <div className="rounded-lg overflow-hidden" style={S.card}>
        <div className="p-5">
          <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: '#1A1A1A', fontSize: '18px' }}>
            End of {timeHorizon}-Year Comparison
          </h3>

          {/* Winner Cards */}
          {displayCalculations.length > 1 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { label: 'Highest NAV', key: 'finalNAV', format: fmt, color: '#2563EB' },
                { label: 'Most BTC', key: 'finalTreasury', format: (v) => fBTC(v, 2) + ' BTC', color: '#92700C' },
                { label: 'Best ROI', key: 'finalROI', format: (v) => (v || 0).toFixed(0) + '%', color: '#16A34A', sub: (v) => { const a = annualizedROI(v || 0, timeHorizon); return a !== null ? `≈ ${a.toFixed(0)}%/yr · ${scenarioLabel}` : scenarioLabel; } },
                { label: 'Most Cash', key: 'finalCash', format: fmt, color: '#0891B2' },
                { label: 'Break-Even', key: 'breakEvenMonth', format: (v) => v ? `Mo ${v}` : 'N/A', color: '#7C3AED' },
              ].map(metric => {
                const winner = displayCalculations.reduce((best, calc) => {
                  const bv = best.results[metric.key] || 0;
                  const cv = calc.results[metric.key] || 0;
                  if (metric.key === 'breakEvenMonth') {
                    if (!cv || cv === 0) return best;
                    if (!bv || bv === 0) return calc;
                    return cv < bv ? calc : best;
                  }
                  return cv > bv ? calc : best;
                });
                return (
                  <div key={metric.key} className="rounded p-4" style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', borderLeft: `3px solid ${metric.color}` }}>
                    <div className="text-xs font-medium mb-2" style={{ color: '#525252' }}>{metric.label}</div>
                    <div className="text-xl font-bold mb-1" style={{ fontVariantNumeric: 'tabular-nums', color: '#1A1A1A' }}>{metric.format(winner.results[metric.key])}</div>
                    <div className="text-xs font-medium" style={{ color: winner.strategy.color }}>{winner.strategy.name}</div>
                    {metric.sub && <div className="text-[10px] mt-0.5" style={{ color: '#737373' }}>{metric.sub(winner.results[metric.key])}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Mining Multiplier (after tax write-offs; that's the comparison) ── */}
          {activeCalc?.miningMultiplier > 0 && (() => {
            const grossMult = activeCalc.miningMultiplier;
            const capExForMult = activeCalc.totalCapExPaid || activeCalc.totalCapEx || 0;
            const grossCosts = capExForMult + (activeCalc.monthlyData || []).reduce((s, d) => s + d.totalOpEx, 0);
            const btcValue = grossCosts * grossMult;
            const taxSavings = miningEconomics?.taxSavings || 0;
            const hasTax = taxSavings > 0 && grossCosts - taxSavings > 0;
            const trueCosts = hasTax ? grossCosts - taxSavings : grossCosts;
            const mult = trueCosts > 0 ? btcValue / trueCosts : grossMult;
            const isPositive = mult >= 1;
            const barWidth = Math.min(mult / 3 * 100, 100); // Scale: 3× = full bar

            return (
              <div className="mb-5 rounded-lg overflow-hidden" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5' }}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#F5F5F5' }}>
                        <Zap className="w-5 h-5" style={{ color: isPositive ? '#92700C' : '#DC2626' }} />
                      </div>
                      <div>
                        <div className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>Mining Return Multiple</div>
                        <div className="text-xs" style={{ color: '#525252' }}>{activeStrategy.name} · {timeHorizon}-year horizon{hasTax ? ' · after tax write-offs' : ''}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold" style={{ color: isPositive ? '#92700C' : '#DC2626', fontVariantNumeric: 'tabular-nums' }}>
                        {mult.toFixed(2)}×
                      </div>
                      {hasTax && (
                        <div className="text-[10px]" style={{ color: '#737373', fontVariantNumeric: 'tabular-nums' }}>
                          {grossMult.toFixed(2)}× before write-offs
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Plain English Explanation */}
                  <div className="rounded p-3 mb-3" style={{ background: '#FFFFFF', border: '1px solid #E5E5E5' }}>
                    <div className="text-sm font-medium" style={{ color: '#1A1A1A' }}>
                      {isPositive
                        ? <>For every <strong>$1</strong> of true cost, <strong style={{ color: '#92700C' }}>${mult.toFixed(2)}</strong> worth of Bitcoin is produced (before any BTC sales).</>
                        : <>Mining currently costs more than the BTC produced. Each <strong>$1</strong> of true cost only produces <strong style={{ color: '#DC2626' }}>${mult.toFixed(2)}</strong> in BTC.</>
                      }
                    </div>
                    <div className="text-xs mt-1.5" style={{ color: '#525252' }}>
                      {hasTax
                        ? <>{fmt(grossCosts)} spent on hardware, electricity, and hosting, minus {fmt(taxSavings)} returned by depreciation and expense write-offs, is a true cost of {fmt(trueCosts)}, against BTC mined worth {fmt(btcValue)} at projected prices</>
                        : <>Total spent: {fmt(grossCosts)} on hardware, electricity and hosting, against BTC mined worth {fmt(btcValue)} at projected prices</>
                      }
                    </div>
                  </div>

                  {/* Visual Bar */}
                  <div>
                    <div className="flex justify-between text-xs mb-1.5" style={{ color: '#737373' }}>
                      <span>0×</span>
                      <span style={{ color: '#525252', fontWeight: 600 }}>1× breakeven</span>
                      <span>3×</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ background: '#E5E5E5' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${barWidth}%`,
                          background: mult >= 2 ? '#16A34A' : mult >= 1 ? '#92700C' : '#DC2626',
                        }}
                      />
                    </div>
                    {/* Breakeven marker at 1/3 of bar */}
                    <div className="relative" style={{ height: 0 }}>
                      <div className="absolute" style={{ left: '33.33%', top: '-17px', transform: 'translateX(-50%)' }}>
                        <div style={{ width: 2, height: 17, background: '#525252', margin: '0 auto' }} />
                      </div>
                    </div>
                  </div>

                  {/* Bottom stats */}
                  <div className="grid grid-cols-3 gap-3 mt-4 pt-3" style={{ borderTop: '1px solid #E5E5E5' }}>
                    <div>
                      <div className="text-xs" style={{ color: '#737373' }}>{hasTax ? 'True Cost (after write-offs)' : 'Total Costs'}</div>
                      <div className="font-semibold text-sm" style={{ color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{fmt(trueCosts)}</div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: '#737373' }}>BTC Value Mined</div>
                      <div className="font-semibold text-sm" style={{ color: isPositive ? '#16A34A' : '#DC2626', fontVariantNumeric: 'tabular-nums' }}>{fmt(btcValue)}</div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: '#737373' }}>Net Gain / Loss</div>
                      <div className="font-semibold text-sm" style={{ color: isPositive ? '#16A34A' : '#DC2626', fontVariantNumeric: 'tabular-nums' }}>
                        {btcValue - trueCosts >= 0 ? '+' : ''}{fmt(btcValue - trueCosts)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Same Dollars Comparison: Mining vs DCA vs S&P ── */}
          {equivalentDCA && (
            <div className="mb-5 rounded-lg overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid #E5E5E5' }}>
              <div className="p-5">
                <div className="mb-4">
                  <h4 className="font-semibold text-base" style={{ color: '#1A1A1A' }}>Same Dollars, Three Paths</h4>
                  <p className="text-xs mt-0.5" style={{ color: '#737373' }}>
                    {fmt(equivalentDCA.totalCashDeployed)} deployed. Here's what it becomes on each path:
                  </p>
                </div>

                {/* Three-column comparison */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Mining */}
                  <div className="rounded p-4" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5' }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: '#92700C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Mining
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>BTC Held (Treasury)</div>
                        <div className="text-xl font-bold" style={{ color: '#92700C', fontVariantNumeric: 'tabular-nums' }}>{fBTC(equivalentDCA.miningTreasury, 4)} BTC</div>
                      </div>
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>Pre-Tax NAV</div>
                        <div className="text-lg font-bold" style={{ color: '#2563EB', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.miningNAV)}</div>
                      </div>
                      {equivalentDCA.totalTaxSavings > 0 && (
                        <div>
                          <div className="text-xs" style={{ color: '#737373' }}>Tax Savings (Dep + OpEx)</div>
                          <div className="text-lg font-bold" style={{ color: '#16A34A', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.totalTaxSavings)}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>Cash Balance</div>
                        <div className="text-sm font-medium" style={{ color: equivalentDCA.miningCash >= 0 ? '#16A34A' : '#DC2626', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.miningCash)}</div>
                      </div>
                      {equivalentDCA.miningCostPerBTC > 0 && (
                        <div className="pt-2 mt-1" style={{ borderTop: '1px solid #E5E5E5' }}>
                          <div className="text-xs" style={{ color: '#737373' }}>Cost/BTC (Gross)</div>
                          <div className="text-sm font-medium" style={{ color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.miningCostPerBTC)}</div>
                          {equivalentDCA.totalTaxSavings > 0 && (
                            <>
                              <div className="text-xs mt-1" style={{ color: '#737373' }}>Cost/BTC (After Tax)</div>
                              <div className="text-sm font-bold" style={{ color: '#16A34A', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.miningAfterTaxCostPerBTC)}</div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DCA BTC */}
                  <div className="rounded p-4" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5' }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      DCA into Bitcoin
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>BTC Accumulated</div>
                        <div className="text-xl font-bold" style={{ color: '#2563EB', fontVariantNumeric: 'tabular-nums' }}>{fBTC(equivalentDCA.dcaBtcAccum, 4)} BTC</div>
                      </div>
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>Portfolio Value</div>
                        <div className="text-lg font-bold" style={{ color: '#2563EB', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.dcaBtcValue)}</div>
                      </div>
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>Avg Cost per BTC</div>
                        <div className="text-sm font-medium" style={{ color: '#525252', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.dcaCostPerBTC)}</div>
                      </div>
                      <div className="pt-2 mt-1" style={{ borderTop: '1px solid #E5E5E5' }}>
                        <div className="text-xs" style={{ color: '#737373' }}>Tax Deductions</div>
                        <div className="text-sm font-medium" style={{ color: '#DC2626' }}>$0 in write-offs</div>
                      </div>
                    </div>
                  </div>

                  {/* S&P 500 */}
                  <div className="rounded p-4" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5' }}>
                    <div className="text-xs font-semibold mb-3" style={{ color: '#16A34A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      S&P 500 Index
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>Portfolio Value</div>
                        <div className="text-xl font-bold" style={{ color: '#16A34A', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.spValue)}</div>
                      </div>
                      <div>
                        <div className="text-xs" style={{ color: '#737373' }}>Assumed Return</div>
                        <div className="text-sm font-medium" style={{ color: '#525252' }}>10% annual</div>
                      </div>
                      <div className="pt-2 mt-1" style={{ borderTop: '1px solid #E5E5E5' }}>
                        <div className="text-xs" style={{ color: '#737373' }}>Tax Deductions</div>
                        <div className="text-sm font-medium" style={{ color: '#DC2626' }}>$0 in write-offs</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax advantage: one line, data first */}
                {equivalentDCA.totalTaxSavings > 0 && (
                  <div className="rounded px-4 py-3 mb-4 flex items-center gap-3 flex-wrap" style={{ background: '#F5F5F5', border: '1px solid #E5E5E5' }}>
                    <Zap className="w-4 h-4 flex-shrink-0" style={{ color: '#16A34A' }} />
                    <span className="text-sm" style={{ color: '#333333' }}>
                      Write-offs return <strong style={{ color: '#16A34A', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.totalTaxSavings)}</strong>
                      {' '}({fmt(equivalentDCA.depreciationSavings)} depreciation + {fmt(equivalentDCA.opExTaxSavings)} expenses),
                      cutting {equivalentDCA.miningGrossCost > 0 ? ((equivalentDCA.totalTaxSavings / equivalentDCA.miningGrossCost) * 100).toFixed(0) : 0}% of
                      gross mining cost. DCA gets none.
                    </span>
                  </div>
                )}

                {/* Winner banner — this plan against the benchmarks */}
                {(() => {
                  const miningValue = equivalentDCA.miningNAV;
                  const results = [
                    { label: 'Mining', value: miningValue, color: '#92700C' },
                    { label: 'DCA Bitcoin', value: equivalentDCA.dcaBtcValue, color: '#2563EB' },
                    { label: 'S&P 500', value: equivalentDCA.spValue, color: '#16A34A' },
                  ].sort((a, b) => b.value - a.value);
                  const winner = results[0];
                  const runnerUp = results[1];
                  const margin = runnerUp.value > 0 ? ((winner.value - runnerUp.value) / runnerUp.value * 100).toFixed(0) : 0;
                  const miningWins = winner.label.startsWith('Mining');
                  const btcDelta = equivalentDCA.miningTreasury - equivalentDCA.dcaBtcAccum;

                  return (
                    <div className="rounded-lg p-4" style={{
                      background: miningWins ? '#F5F5F5' : '#F5F5F5',
                      border: `1px solid ${miningWins ? '#E5E5E5' : '#E5E5E5'}`,
                    }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-bold" style={{ color: miningWins ? '#92700C' : '#DC2626' }}>
                            {miningWins
                              ? `Mining wins by ${margin}% over ${runnerUp.label}`
                              : `${winner.label} wins, ${fmt(winner.value)} vs Mining at ${fmt(miningValue)}`
                            }
                          </div>
                          <div className="text-xs mt-1" style={{ color: '#525252' }}>
                            {btcDelta > 0
                              ? `Mining yields ${fBTC(btcDelta, 4)} more BTC than DCA (${fBTC(equivalentDCA.miningTreasury, 4)} vs ${fBTC(equivalentDCA.dcaBtcAccum, 4)})`
                              : `DCA yields ${fBTC(Math.abs(btcDelta), 4)} more BTC than mining (${fBTC(equivalentDCA.dcaBtcAccum, 4)} vs ${fBTC(equivalentDCA.miningTreasury, 4)})`
                            }
                          </div>
                          {equivalentDCA.totalTaxSavings > 0 && !miningWins && (
                            <div className="text-xs mt-1" style={{ color: '#525252' }}>
                              Mining also generates {fmt(equivalentDCA.totalTaxSavings)} in tax savings (depreciation + OpEx deductions)
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xs" style={{ color: '#737373' }}>Total Deployed</div>
                          <div className="text-sm font-bold" style={{ color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{fmt(equivalentDCA.totalCashDeployed)}</div>
                        </div>
                      </div>

                      {!miningWins && !(equivalentDCA.totalTaxSavings > 0) && (
                        <div className="mt-3 pt-2 text-xs" style={{ borderTop: '1px solid #E5E5E5', color: '#525252' }}>
                          Enable tax analysis in the sidebar to see mining's tax advantage. Mining's edge comes from 100% bonus depreciation + OpEx deductions.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── Comparison Table ── */}
          <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid #E5E5E5' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#FCFCFC' }}>
                  <th className="text-left py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>Strategy</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>CapEx</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>OpEx ({timeHorizon}yr)</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>BTC Acq.</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>Cost/BTC</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>Treasury</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>NAV</th>
                  <th className="text-right py-3 px-4 font-medium" style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>ROI</th>
                </tr>
              </thead>
              <tbody>
                {displayCalculations.map((calc, i) => {
                  const totalOpExCalc = calc.results.annualData?.reduce((s, y) => s + (y.opEx || 0), 0) || 0;
                  const capExForCost = calc.results.totalCapExPaid || calc.results.totalCapEx || 0;
                  const totalDeployed = capExForCost + totalOpExCalc;
                  const btcMinedCalc = calc.results.totalBTCMined || 0;
                  // For DCA BTC: cost/BTC = totalInvested / btcPurchased; for mining: (capex+opex)/btcMined
                  const btcPurchased = calc.results.btcPurchased || 0;
                  const costPerBTC = btcMinedCalc > 0
                    ? totalDeployed / btcMinedCalc
                    : (btcPurchased > 0 ? (calc.results.totalInvested || capExForCost) / btcPurchased : 0);
                  const hasCostBTC = costPerBTC > 0;
                  return (
                    <tr key={calc.strategy.id || i} className="transition-colors" style={{ borderBottom: i < displayCalculations.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: calc.strategy.color }} />
                          <span className="font-medium" style={{ color: '#1A1A1A' }}>{calc.strategy.name}</span>
                        </div>
                        {calc.strategy.isBenchmark && calc.results.btcPurchased > 0 && (
                          <span className="text-xs ml-5" style={{ color: '#737373' }}>{fBTC(calc.results.btcPurchased, 2)} bought</span>
                        )}
                      </td>
                      <td className="text-right py-3 px-4" style={S.tn('#525252')}>{fmt(capExForCost)}</td>
                      <td className="text-right py-3 px-4" style={S.tn('#525252')}>{fmt(totalOpExCalc)}</td>
                      <td className="text-right py-3 px-4" style={S.tn('#92700C')}>{fBTC(btcMinedCalc || btcPurchased, 2)}</td>
                      <td className="text-right py-3 px-4" style={S.tn(calc.strategy.isBenchmark ? '#2563EB' : '#1A1A1A')}>
                        {hasCostBTC ? fmt(costPerBTC) : '—'}
                      </td>
                      <td className="text-right py-3 px-4 font-semibold" style={S.tn('#92700C')}>{fBTC(calc.results.finalTreasury || 0, 2)} {calc.strategy.name?.includes('S&P') ? 'eq.' : 'BTC'}</td>
                      <td className="text-right py-3 px-4 font-semibold" style={S.tn('#2563EB')}>{fmt(calc.results.finalNAV || 0)}</td>
                      <td className="text-right py-3 px-4 font-semibold" style={S.tn('#16A34A')}>{(calc.results.finalROI || 0).toFixed(0)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[10px] mt-2" style={{ color: '#737373', lineHeight: 1.5 }}>
              All figures assume {scenarioLabel} BTC price growth over {timeHorizon} years. Change the
              scenario in Market Settings to stress-test.
            </div>
          </div>
        </div>
      </div>

      {/* ── Comparison Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[
          { key: 'nav', formatter: fmt, type: 'area' },
          { key: 'treasury', formatter: (v) => fBTC(v, 4) + ' BTC', type: 'area' },
          { key: 'roi', formatter: (v) => (v || 0).toFixed(0) + '%', type: 'line' },
          { key: 'cash', formatter: fmt, type: 'line' },
        ].map((chart) => {
          const meta = chartMeta[chart.key];
          return (
            <div key={chart.key} className="rounded-lg overflow-hidden" style={S.card}>
              <div className="p-5">
                {/* Chart header */}
                <div className="mb-4">
                  <h3 className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{meta.title}</h3>
                  <p className="text-xs mt-0.5" style={{ color: '#737373' }}>{meta.subtitle}</p>
                </div>

                <ResponsiveContainer width="100%" height={280}>
                  {chart.type === 'area' ? (
                    <AreaChart data={comparisonData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <defs>
                        {displayCalculations.map((calc, i) => (
                          <linearGradient key={i} id={`grad_${chart.key}_${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={calc.strategy.color} stopOpacity={0.2} />
                            <stop offset="85%" stopColor={calc.strategy.color} stopOpacity={0.01} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#E5E5E5" />
                      <XAxis
                        dataKey="month"
                        stroke="transparent"
                        tick={{ fontSize: 10, fill: '#737373' }}
                        tickFormatter={(m) => monthTick(m, timeHorizon)}
                        interval={timeHorizon <= 2 ? 2 : 11}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="transparent"
                        tick={{ fontSize: 10, fill: '#737373' }}
                        tickFormatter={chart.key === 'treasury' ? (v) => v.toFixed(1) : fmt}
                        axisLine={false}
                        tickLine={false}
                        width={65}
                      />
                      <Tooltip content={<SortedTooltip formatter={chart.formatter} />} />
                      {displayCalculations.map((calc, i) => (
                        <Area
                          key={i}
                          type="monotone"
                          dataKey={`${chart.key}_${i}`}
                          name={calc.strategy.name}
                          stroke={calc.strategy.color}
                          strokeWidth={2}
                          fill={`url(#grad_${chart.key}_${i})`}
                          dot={false}
                          activeDot={{ r: 4, fill: '#fff', stroke: calc.strategy.color, strokeWidth: 2.5 }}
                          strokeDasharray={calc.strategy.isBenchmark ? '6 3' : '0'}
                        />
                      ))}
                    </AreaChart>
                  ) : (
                    <LineChart data={comparisonData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#E5E5E5" />
                      <XAxis
                        dataKey="month"
                        stroke="transparent"
                        tick={{ fontSize: 10, fill: '#737373' }}
                        tickFormatter={(m) => monthTick(m, timeHorizon)}
                        interval={timeHorizon <= 2 ? 2 : 11}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="transparent"
                        tick={{ fontSize: 10, fill: '#737373' }}
                        tickFormatter={chart.key === 'roi' ? (v) => v.toFixed(0) + '%' : fmt}
                        axisLine={false}
                        tickLine={false}
                        width={65}
                      />
                      <Tooltip content={<SortedTooltip formatter={chart.formatter} />} />
                      <ReferenceLine y={0} stroke="#737373" strokeDasharray="4 4" />
                      {displayCalculations.map((calc, i) => (
                        <Line
                          key={i}
                          type="monotone"
                          dataKey={`${chart.key}_${i}`}
                          name={calc.strategy.name}
                          stroke={calc.strategy.color}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4, fill: '#fff', stroke: calc.strategy.color, strokeWidth: 2.5 }}
                          strokeDasharray={calc.strategy.isBenchmark ? '6 3' : '0'}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
                <ChartLegend items={legendItems} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
